import { useMemo, Dispatch, SetStateAction } from 'react'
import { Button } from 'primereact/button'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredGymTrainerList } from 'types/GymType'
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import TrainerPhoto from '../TrainerPhoto'

interface ModalHeaderProps {
  availableDateRange: { start: number; end: number }
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  setSelectedTrainer: Dispatch<SetStateAction<IFilteredGymTrainerList | undefined>>
  trainer: IFilteredGymTrainerList | undefined
  trainers: IFilteredGymTrainerList[] | []
  setTableWeek: Dispatch<SetStateAction<{ start: Date; end: Date }>>
  selectedDateStr?: string
}

export default function ReservationModalHeader({
  availableDateRange: availableDates,
  selectedDateStr,
  filterDate,
  setFilterDate,
  setTableWeek,
  trainer,
  trainers,
  setSelectedTrainer,
}: ModalHeaderProps) {
  const { t } = useTranslation()
  const { id, priceStr, price, gymSaloon, gymTrainer, gymTrainerService } = trainer || {}
  const { locationName: floorLocationName, districtName, cityName, countryName, blockName, name } = gymSaloon?.floor || {}

  // Variables
  const fullName = gymTrainer?.fullName
  const locationName = `${floorLocationName}, ${districtName}, ${cityName}, ${countryName}, ${blockName} ${name}`
  const priceVal = !!price ? priceStr : t('most-used-words.price-free')
  const serviceTypes = gymTrainerService?.map((st) => st.gymTrainerServiceType.name).join(', ')
  const length = trainers?.length
  const btnDisabled = length <= 1
  const currentSlideIndex = useMemo(() => trainers.findIndex((pt) => pt.id === trainer?.id), [trainers, trainer?.id])

  /* Trainer Slider */
  const nextSlide = () =>
    currentSlideIndex === length - 1
      ? setSelectedTrainer(trainers[0])
      : setSelectedTrainer(trainers[currentSlideIndex + 1])
  const prevSlide = () =>
    currentSlideIndex === 0
      ? setSelectedTrainer(trainers[length - 1])
      : setSelectedTrainer(trainers[currentSlideIndex - 1])

  /* Handle Reservation Date input change */
  const handleDateChange = (e: any) => {
    if (e.value) {
      const { start, end } = DateUtil.getWeekStartEndFromDate(e.value)
      setTableWeek({ start, end })
      setFilterDate(e.value)
    }
  }

  return (
    <HeaderInfo>
      <div className="trainer-info">
        <div className="slider-left-arrow">
          <Button
            id="trainerPrev"
            disabled={btnDisabled}
            onClick={prevSlide}
            icon="pi pi-chevron-left"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>

        <div className="trainer-photo">
          <TrainerPhoto trainerEmployeeId={id || 0} IsAbsolute={false} size={[54, 54]} />
        </div>

        <div className="trainer-summary">
          <span className="pt-name-surname">{fullName}</span>
          <span className="pt-info-text fw-bold">{serviceTypes || '---'} </span>

          <span className="pt-info-text">{locationName || '---'}</span>
          <span className="pt-info-text">
            <span className="fw-bold">{t('most-used-words.hourly-price')}: </span> {priceVal}
          </span>
        </div>

        <div className="slider-right-arrow">
          <Button
            id="trainerNext"
            disabled={btnDisabled}
            onClick={nextSlide}
            icon="pi pi-chevron-right"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center justify-content-end">
        {!!selectedDateStr ? (
          <div className="calendar-notify">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</div>
        ) : null}
      </div>

      <div className="appointment-date">
        <DatePickerIcon
          idName="trainerModalReservationDate"
          showOtherMonths
          selectOtherMonths
          showOnFocus
          appendTo={document.body}
          disabledDays={[]}
          defaultDate={filterDate}
          minDate={DateUtil.getPastDate(availableDates.start)}
          maxDate={DateUtil.getFutureDate(availableDates.end)}
          label={t('input-texts.appointment-date')}
          onSelectedDateChange={handleDateChange}
          onFocus={() => {}}
        />
      </div>
    </HeaderInfo>
  )
}

const HeaderInfo = styled.div`
  margin: 1rem 0 0.5rem 0;
  display: flex;
  justify-content: space-between;

  .trainer-info {
    position: relative;
    width: 400px;
    display: flex;
    align-items: flex-start;

    .slider-left-arrow {
      position: absolute;
      top: 0;
      left: 0;
    }

    .slider-right-arrow {
      position: absolute;
      top: 0;
      right: 0;
    }

    .trainer-photo {
      width: 55px;
      height: 55px;
      margin: 0 10px 0 40px;
    }

    .trainer-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin-right: 40px;

      .pt-info-text {
        font-size: 12px;
        font-weight: 500;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }

      .pt-name-surname {
        font-size: 14px;
        font-weight: 700;
      }
    }
  }

  .calendar-notify {
    max-width: 410px;
    margin: 1rem 0;
    font-size: 14px;
    color: #222e47;
    line-height: 17px;
    font-weight: 700;
  }

  .appointment-date {
    min-width: 265px;
  }
`
